import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BalanceService } from '@manager-app/service/balance.service';
import { HxErrorHandlerService, HxLoaderComponent, HxProductCategorySelectComponent, HxStoreSelectComponent } from 'hx-component';
import { Subject } from 'rxjs';
import { HxAuthService, HxCategoryService, HxMoneyFormatPipe, HxStoreProductService, HxUiLabelPipe, ProductCategoryModel, ProductInfoType, StoreBasicModel, StoreProductModel } from 'hx-services';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { HcIconComponent } from '../hc-icon/hc-icon.component';
import { StoreProductComponent } from '../store-product/store-product.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import writeXlsxFile from 'write-excel-file';

type tab = 'storage' | 'service' | 'materialAssets' | 'nonMaterialAssets';

@Component({
    selector: 'app-balance-view',
    templateUrl: './balance-view.component.html',
    styleUrls: ['./balance-view.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, NgIf, NgFor, NgClass, HxProductCategorySelectComponent, HcIconComponent, StoreProductComponent, HxLoaderComponent, TranslocoPipe, HxMoneyFormatPipe],
    providers: [HxUiLabelPipe]
})
export class BalanceViewComponent implements OnInit, OnDestroy {
  @Input() storeId?: number;
  @Output() storeChange = new EventEmitter<number | undefined>();
  products: StoreProductModel[] = [];
  totalStockValue = 0;
  showStockValue = false;
  isLoading = {
    storeBalance: false,
    category: false,
  };
  categoryIds: number[] = [];
  productInfoTitle?: string;
  productInfoTypes: { id: tab, label: string, types: ProductInfoType[] }[] = [
    {id: 'storage', label: 'label.storage', types: [ProductInfoType.PRODUCT, ProductInfoType.INGREDIENT, ProductInfoType.PROPERTY]},
    {id: 'service', label: 'label.service', types: [ProductInfoType.SERVICE]},
    {id: 'materialAssets', label: 'label.materialAssets', types: [ProductInfoType.MATERIAL_ASSETS]},
    {id: 'nonMaterialAssets', label: 'label.nonMaterialAssets', types: [ProductInfoType.NON_MATERIAL_ASSETS]}
  ];
  selectedProductInfoTypes?: ProductInfoType[] = [ProductInfoType.PRODUCT, ProductInfoType.INGREDIENT, ProductInfoType.PROPERTY];
  productInfoTypeId: tab = 'storage';
  brandId?: number;
  private $destroyed = new Subject<void>();

  constructor(
    private balanceService: BalanceService,
    private categoryService: HxCategoryService,
    private storeProductService: HxStoreProductService,
    private router: Router,
    private aRoute: ActivatedRoute,
    private auth: HxAuthService,
    private errorService: HxErrorHandlerService,
    private uiLabelPipe: HxUiLabelPipe,
    private translocoService: TranslocoService,
  ) {
  }

  ngOnInit() {
    this.brandId = localStorage.getItem('defaultBrandId') ? Number(localStorage.getItem('defaultBrandId')) : undefined;
    this.balanceService.updateBalanceObs.pipe(takeUntil(this.$destroyed)).subscribe(() => this.loadBalance());
  }

  ngOnDestroy() {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  searchProduct() {
    if (!this.productInfoTitle) {
      return;
    }
    this.loadBalance();
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (Array.isArray(store)) {
      return;
    }
    this.storeId = store?.id;
    this.brandId = store?.brandId;
    this.storeChange.emit(store?.id);
    this.loadBalance();
  }

  selectProductInfoType(id: tab, types: ProductInfoType[]) {
    this.selectedProductInfoTypes = types;
    this.productInfoTypeId = id;
    this.productInfoTitle = undefined;
    if (id !== 'storage') {
      this.categoryIds = [];
    }
    this.loadBalance();
  }

  resetFilters() {
    this.categoryIds = [];
    this.productInfoTitle = undefined;
    this.loadBalance();
  }

  onProductCategoryChanged(event: ProductCategoryModel | ProductCategoryModel[] | undefined) {
    if (event) {
      if (Array.isArray(event)) {
        if (event.length === 0) {
          this.categoryIds = [];
        } else {
          this.categoryIds.push(event[0].id);
        }
      } else if (!Array.isArray(event)) {
        this.categoryIds.push(event.id);
      }
    }
    this.loadBalance();
  }

  async downloadXlsx() {
    const nameHeader = this.translocoService.translate('balance-view.export.name');
    const balanceHeader = this.translocoService.translate('balance-view.export.balance');
    const data = [
      [
        { value: nameHeader, fontWeight: 'bold' as const },
        { value: balanceHeader, fontWeight: 'bold' as const },
      ],
      ...this.products
        .filter(p => p.productInfo?.id && p.amount)
        .map(p => [
          { type: String, value: this.uiLabelPipe.transform(p.productInfo.title) },
          { type: String, value: `${p.amount} ${this.uiLabelPipe.transform(p.unitOfMeasure?.globalShortTitle)}` },
        ]),
    ];
    const date = new Date();
    const dateSuffix = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    await writeXlsxFile(data, { fileName: `inventory-${dateSuffix}.xlsx` });
  }

  private recalculateTotalStockValue() {
    this.totalStockValue = this.products
      .filter(p => p.productInfo?.id && p.amount)
      .reduce((sum, p) => sum + p.amount * (p.costPrice ?? 0), 0);
  }

  // Блок «Стоимость склада» + «Скачать» показываем только для закупаемого сырья (purchasable) —
  // у него есть реальная себестоимость. Выпускаемые позиции (полуфабрикаты/торты — cfOutputProduct,
  // себестоимость пока 0) скрыты до рецептурного расчёта. Признак intrinsic, без привязки к id/имени категории.
  private recalcShowStockValue() {
    this.showStockValue = this.products.length > 0
      && this.products.every(p => p.productInfo?.purchasable === true);
  }

  private loadBalance() {
    if (!this.storeId) {
      return;
    }
    this.totalStockValue = 0;
    this.isLoading.storeBalance = true;
    this.products = [];
    this.totalStockValue = 0;
    this.showStockValue = false;
    if (this.productInfoTypeId !== 'storage' || (this.categoryIds && this.categoryIds.length)) {
      this.storeProductService.getBalance({
        storeId: this.storeId,
        categoryIds: this.categoryIds,
        productInfoTitle: this.productInfoTitle,
        productInfoTypes: this.selectedProductInfoTypes
      }).subscribe(result => {
        this.products = result;
        this.recalculateTotalStockValue();
        this.recalcShowStockValue();
        this.isLoading.storeBalance = false;
      }, err => {
        this.isLoading.storeBalance = false;
        this.errorService.check(err.error);
      });
    } else {
      this.isLoading.storeBalance = false;
    }
  }
}
